import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyLeads, chooseLeadCompany } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Sparkles, MapPin, CheckCircle2, Clock, XCircle, MessageCircle, ArrowRight, Award, Building2 } from 'lucide-react';

const LEAD_STATUS = {
  pending: { label: 'Afventer svar', color: 'bg-[#f5e6d8] text-[#6b3e25]', icon: Clock },
  accepted: { label: 'Håndværker valgt', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  no_match: { label: 'Ingen match', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const ASSIGNMENT_LABEL = {
  pending: { label: 'Afventer', color: 'text-stone-500' },
  interested: { label: 'Vil gerne tage opgaven', color: 'text-green-700' },
  rejected: { label: 'Har afvist', color: 'text-red-500' },
  chosen: { label: 'Valgt af dig', color: 'text-[#c97f59] font-semibold' },
  closed: { label: 'Ikke valgt', color: 'text-stone-400' },
};

export default function MyLeadsSection() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [choosing, setChoosing] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getMyLeads();
      setLeads(r.data);
    } catch (e) {
      toast.error('Kunne ikke hente leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChoose = async (leadId, companyId) => {
    setChoosing(`${leadId}:${companyId}`);
    try {
      const r = await chooseLeadCompany(leadId, companyId);
      toast.success('Håndværker valgt – chatten er åben');
      navigate(`/chat/${r.data.conversation_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke vælge håndværker');
    } finally {
      setChoosing('');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="my-leads-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#c97f59]" />
            Automatiske anmodninger
          </CardTitle>
          <CardDescription>Håndværkere matchet på afstand & kapacitet</CardDescription>
        </div>
        <Button onClick={() => navigate('/find-automatisk')} className="bronze-shine border-0 text-white" data-testid="new-lead-button">
          <Sparkles className="w-4 h-4 mr-2" />
          Ny anmodning
        </Button>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-10">
            <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium mb-1">Ingen automatiske anmodninger endnu</p>
            <p className="text-sm text-muted-foreground mb-4">Lad os finde de bedste håndværkere til dig</p>
            <Button onClick={() => navigate('/find-automatisk')} className="bronze-shine border-0 text-white" data-testid="empty-new-lead-button">
              Kom i gang
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {leads.map((lead) => {
              const s = LEAD_STATUS[lead.status] || LEAD_STATUS.pending;
              const StatusIcon = s.icon;
              const interested = (lead.assignments || []).filter((a) => a.status === 'interested');

              return (
                <div key={lead.id} className="rounded-xl border p-5 space-y-4" data-testid={`lead-${lead.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{lead.category}</h4>
                        <Badge className={s.color}><StatusIcon className="w-3 h-3 mr-1" />{s.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.postal_code}</span>
                        <span>{new Date(lead.created_at).toLocaleDateString('da-DK')}</span>
                      </p>
                    </div>
                    {lead.status === 'accepted' && lead.conversation_id && (
                      <Button size="sm" variant="secondary" onClick={() => navigate(`/chat/${lead.conversation_id}`)} data-testid={`lead-chat-${lead.id}`}>
                        <MessageCircle className="w-4 h-4 mr-1.5" />Åbn chat
                      </Button>
                    )}
                  </div>

                  <p className="text-sm">{lead.description}</p>

                  {/* Interested companies – user picks */}
                  {lead.status === 'pending' && interested.length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <p className="text-sm font-semibold text-[#c97f59] flex items-center gap-1">
                        <Award className="w-4 h-4" />
                        {interested.length} håndværker{interested.length > 1 ? 'e' : ''} vil gerne tage opgaven – vælg én
                      </p>
                      {interested.map((a) => (
                        <div key={a.company_id} className="flex items-center justify-between p-3 rounded-lg bg-stone-50 border">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{a.company_name}</p>
                            <p className="text-xs text-muted-foreground">{a.distance_km} km fra dig</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/company/${a.company_id}`)} data-testid={`view-lead-company-${lead.id}-${a.company_id}`}>
                              Se profil
                            </Button>
                            <Button
                              size="sm"
                              className="bronze-shine border-0 text-white"
                              disabled={choosing === `${lead.id}:${a.company_id}`}
                              onClick={() => handleChoose(lead.id, a.company_id)}
                              data-testid={`choose-lead-company-${lead.id}-${a.company_id}`}
                            >
                              {choosing === `${lead.id}:${a.company_id}` ? 'Vælger...' : (<>Vælg<ArrowRight className="w-3 h-3 ml-1" /></>)}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* All assignments summary */}
                  {(lead.assignments || []).length > 0 && (
                    <details className="text-xs text-muted-foreground">
                      <summary className="cursor-pointer hover:text-foreground">Vis alle matches ({lead.assignments.length})</summary>
                      <div className="mt-2 space-y-1">
                        {lead.assignments.map((a) => {
                          const l = ASSIGNMENT_LABEL[a.status] || { label: a.status, color: 'text-stone-500' };
                          return (
                            <div key={a.company_id} className="flex items-center justify-between gap-2">
                              <span className="truncate flex items-center gap-1.5"><Building2 className="w-3 h-3" />{a.company_name} · {a.distance_km} km</span>
                              <span className={l.color}>{l.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}

                  {lead.status === 'pending' && interested.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">Vi venter på svar fra håndværkerne. Du får besked så snart nogen viser interesse.</p>
                  )}

                  {lead.status === 'no_match' && (
                    <p className="text-xs text-red-600">Ingen af de matchede håndværkere kunne tage opgaven. Prøv at oprette en ny anmodning med et bredere område.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
